import React, { useEffect, useState } from 'react';
import authService from '../services/authService';
import { getImageUrl } from '../utils/imageHelper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BoardGamesManager = ({ club }) => {
  const [clubBoardGames, setClubBoardGames] = useState([]);
  const [allBoardGames, setAllBoardGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [linkFormData, setLinkFormData] = useState({
    available: true,
    quantity: 1,
    notes: ''
  });
  const [requestFormData, setRequestFormData] = useState({
    name: '',
    description: '',
    rules: '',
    minPlayers: 2,
    maxPlayers: 4,
    duration: '',
    complexity: 'medium',
    designer: '',
    publisher: '',
    yearPublished: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (club) {
      loadClubBoardGames();
    }
  }, [club]);

  useEffect(() => {
    if (showSelectModal) {
      loadAllBoardGames();
    }
  }, [showSelectModal]);

  const loadClubBoardGames = async () => {
    try {
      setLoading(true);
      const result = await authService.get(`${API_URL}/clubs/${club.id}/board-games`);
      if (result.success) {
        setClubBoardGames(result.data);
      }
    } catch (error) {
      console.error('Error loading club board games:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllBoardGames = async () => {
    try {
      const response = await fetch(`${API_URL}/board-games?limit=1000`);
      const result = await response.json();
      if (result.success) {
        // Filter out games already linked to this club
        const linkedGameIds = clubBoardGames.map(cbg => cbg.boardGame.id);
        const availableGames = result.data.filter(game => !linkedGameIds.includes(game.id));
        setAllBoardGames(availableGames);
      }
    } catch (error) {
      console.error('Error loading all board games:', error);
    }
  };

  const handleLinkBoardGame = async (e) => {
    e.preventDefault();
    if (!selectedGame) return;

    setSubmitting(true);
    try {
      const result = await authService.post(
        `${API_URL}/clubs/${club.id}/board-games/link`,
        {
          boardGameId: selectedGame.id,
          available: linkFormData.available,
          quantity: parseInt(linkFormData.quantity),
          notes: linkFormData.notes
        }
      );

      if (result.success) {
        await loadClubBoardGames();
        handleCloseSelectModal();
        alert('Board game added to your club!');
      }
    } catch (error) {
      console.error('Error linking board game:', error);
      alert('Failed to add board game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await authService.post(`${API_URL}/board-game-requests`, {
        ...requestFormData,
        minPlayers: parseInt(requestFormData.minPlayers),
        maxPlayers: parseInt(requestFormData.maxPlayers),
        duration: requestFormData.duration ? parseInt(requestFormData.duration) : null,
        yearPublished: requestFormData.yearPublished ? parseInt(requestFormData.yearPublished) : null
      });

      if (result.success) {
        alert('Board game request submitted! A super admin will review it.');
        handleCloseRequestModal();
      }
    } catch (error) {
      console.error('Error submitting board game request:', error);
      alert('Failed to submit board game request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlinkBoardGame = async (boardGameId) => {
    if (!window.confirm('Remove this board game from your club?')) return;

    try {
      const result = await authService.delete(
        `${API_URL}/clubs/${club.id}/board-games/${boardGameId}`
      );

      if (result.success) {
        await loadClubBoardGames();
        alert('Board game removed from club');
      }
    } catch (error) {
      console.error('Error unlinking board game:', error);
      alert('Failed to remove board game');
    }
  };

  const handleCloseSelectModal = () => {
    setShowSelectModal(false);
    setSelectedGame(null);
    setSearchQuery('');
    setLinkFormData({ available: true, quantity: 1, notes: '' });
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setRequestFormData({
      name: '',
      description: '',
      rules: '',
      minPlayers: 2,
      maxPlayers: 4,
      duration: '',
      complexity: 'medium',
      designer: '',
      publisher: '',
      yearPublished: ''
    });
  };

  const filteredGames = allBoardGames.filter(game =>
    game.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center text-gray-500">Loading board games...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Board Games</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select games from our database or request new ones
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSelectModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Board Game
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Request New Game
            </button>
          </div>
        </div>
      </div>

      {/* Board Games Grid */}
      <div className="p-6">
        {clubBoardGames.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No board games</h3>
            <p className="mt-1 text-sm text-gray-500">Add a board game from our database to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubBoardGames.map((item) => {
              const game = item.boardGame;
              return (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {game.coverImage ? (
                    <img
                      src={getImageUrl(game.coverImage)}
                      alt={game.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <svg className="h-20 w-20 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">{game.name}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.available 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.available ? 'Available' : 'Unavailable'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Quantity: {item.quantity}</p>
                    {item.notes && (
                      <p className="text-xs text-gray-500 mb-3 italic">{item.notes}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <span>👥 {game.minPlayers}-{game.maxPlayers}</span>
                      {game.duration && <span>⏱️ {game.duration} min</span>}
                      {game.complexity && (
                        <span className={`px-2 py-1 rounded ${
                          game.complexity === 'easy' ? 'bg-green-100 text-green-700' :
                          game.complexity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {game.complexity}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnlinkBoardGame(game.id)}
                      className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                    >
                      Remove from Club
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Select Game Modal */}
      {showSelectModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Select a Board Game</h3>
                <button onClick={handleCloseSelectModal} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <input
                type="text"
                placeholder="Search board games..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-4 w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {!selectedGame ? (
              <div className="flex-1 overflow-y-auto p-6">
                {filteredGames.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No board games found. Try requesting a new one!
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredGames.map((game) => (
                      <div
                        key={game.id}
                        onClick={() => setSelectedGame(game)}
                        className="border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:shadow cursor-pointer transition"
                      >
                        <h4 className="font-semibold text-gray-900 mb-2">{game.name}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{game.description}</p>
                        <div className="flex items-center text-xs text-gray-500 gap-3">
                          <span>👥 {game.minPlayers}-{game.maxPlayers}</span>
                          {game.duration && <span>⏱️ {game.duration} min</span>}
                          {game.complexity && <span className="capitalize">{game.complexity}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleLinkBoardGame} className="flex-1 overflow-y-auto p-6">
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-1">{selectedGame.name}</h4>
                  <p className="text-sm text-gray-600">{selectedGame.description}</p>
                  <button
                    type="button"
                    onClick={() => setSelectedGame(null)}
                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    ← Choose a different game
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={linkFormData.available}
                        onChange={(e) => setLinkFormData({ ...linkFormData, available: e.target.checked })}
                        className="mr-2"
                      />
                      Available for play
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={linkFormData.quantity}
                      onChange={(e) => setLinkFormData({ ...linkFormData, quantity: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                    <textarea
                      value={linkFormData.notes}
                      onChange={(e) => setLinkFormData({ ...linkFormData, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Any special notes about this game..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {submitting ? 'Adding...' : 'Add to Club'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseSelectModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Request New Game Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Request New Board Game</h3>
              <p className="text-sm text-gray-500 mt-1">Submit a request for a board game not in our database</p>
            </div>
            <form onSubmit={handleSubmitRequest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Game Name *</label>
                <input
                  type="text"
                  value={requestFormData.name}
                  onChange={(e) => setRequestFormData({ ...requestFormData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={requestFormData.description}
                  onChange={(e) => setRequestFormData({ ...requestFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Players</label>
                  <input
                    type="number"
                    value={requestFormData.minPlayers}
                    onChange={(e) => setRequestFormData({ ...requestFormData, minPlayers: e.target.value })}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Players</label>
                  <input
                    type="number"
                    value={requestFormData.maxPlayers}
                    onChange={(e) => setRequestFormData({ ...requestFormData, maxPlayers: e.target.value })}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={requestFormData.duration}
                    onChange={(e) => setRequestFormData({ ...requestFormData, duration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complexity</label>
                  <select
                    value={requestFormData.complexity}
                    onChange={(e) => setRequestFormData({ ...requestFormData, complexity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designer</label>
                <input
                  type="text"
                  value={requestFormData.designer}
                  onChange={(e) => setRequestFormData({ ...requestFormData, designer: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
                <input
                  type="text"
                  value={requestFormData.publisher}
                  onChange={(e) => setRequestFormData({ ...requestFormData, publisher: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Published</label>
                <input
                  type="number"
                  value={requestFormData.yearPublished}
                  onChange={(e) => setRequestFormData({ ...requestFormData, yearPublished: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseRequestModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardGamesManager;
